import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { InboundListItem } from '@/screens/order-inbounds/types';

interface PendingAddItem {
  confirmed?: boolean;
  quantity: number;
  searchInputUUID?: string;
}

interface OrderInboundDetailState {
  actions: OrderInboundDetailActions;
  itemToCorrect: InboundListItem | null;
  itemToSwap: InboundListItem | null;
  pendingItemNotes: Map<string, string>;
  pendingQuantityMap: Map<string, PendingAddItem>;
  selectedInputUUID?: string;
  showAddItemSheet: boolean;
  updatingLocation: boolean;
}

interface OrderInboundDetailActions {
  clearPendingItemNote: (searchInputUUID: string) => void;
  clearPendingItemNotes: () => void;
  clearPendingQuantityMap: () => void;
  setItemToCorrect: (newItemToCorrect: InboundListItem | null) => void;
  setItemToSwap: (newItemToSwap: InboundListItem | null) => void;
  setPendingItemNote: (searchInputUUID: string, itemNote: string) => void;
  setSelectedInputUUID: (newSelectedInputUUID?: string) => void;
  setShowAddItemSheet: (newShowAddItemSheet: boolean) => void;
  setUpdatingLocation: (newSetUpdatingLocation: boolean) => void;
  updatePendingItem: (variantPackUUID: string, data: PendingAddItem) => void;
}

export const selectHasPendingItems = (
  state: OrderInboundDetailState,
): boolean =>
  Array.from(state.pendingQuantityMap.values()).some(
    (value) => value.quantity !== 0,
  );

export const useOrderInboundDetailStore = create<OrderInboundDetailState>()(
  immer((set) => ({
    actions: {
      clearPendingItemNote: (searchInputUUID) =>
        set((state) => {
          state.pendingItemNotes.delete(searchInputUUID);
        }),
      clearPendingItemNotes: () =>
        set((state) => {
          state.pendingItemNotes.clear();
        }),
      clearPendingQuantityMap: () =>
        set((state) => {
          state.pendingQuantityMap.clear();
        }),
      setItemToCorrect: (newItemToCorrect: InboundListItem | null) =>
        set((state) => {
          state.itemToCorrect = newItemToCorrect;
          const correctItemInputUUID =
            newItemToCorrect?.metadata?.inputWithResult.uuid;
          if (correctItemInputUUID) {
            state.selectedInputUUID = correctItemInputUUID;
          }
        }),
      setItemToSwap: (newItemToSwap: InboundListItem | null) =>
        set((state) => {
          state.itemToSwap = newItemToSwap;
          const swapItemInputUUID =
            newItemToSwap?.metadata?.inputWithResult.uuid;
          if (swapItemInputUUID) {
            state.selectedInputUUID = swapItemInputUUID;
          }
        }),
      setPendingItemNote: (searchInputUUID, itemNote) =>
        set((state) => {
          state.pendingItemNotes.set(searchInputUUID, itemNote);
        }),
      setSelectedInputUUID: (newSelectedInputUUID) =>
        set((state) => {
          state.selectedInputUUID = newSelectedInputUUID;
        }),
      setShowAddItemSheet: (newShowAddItemSheet) =>
        set((state) => {
          state.itemToSwap = null;
          state.showAddItemSheet = newShowAddItemSheet;
        }),
      setUpdatingLocation: (newSetUpdatingLocation) =>
        set((state) => {
          state.updatingLocation = newSetUpdatingLocation;
        }),
      updatePendingItem: (variantPackUUID: string, data: PendingAddItem) =>
        set((state) => {
          const existingData = state.pendingQuantityMap.get(variantPackUUID);
          if (existingData) {
            state.pendingQuantityMap.set(variantPackUUID, {
              ...existingData,
              ...data,
            });
          } else {
            state.pendingQuantityMap.set(variantPackUUID, data);
          }
        }),
    },
    itemToCorrect: null,
    itemToSwap: null,
    pendingItemNotes: new Map<string, string>(),
    pendingQuantityMap: new Map<string, PendingAddItem>(),
    showAddItemSheet: false,
    updatingLocation: false,
  })),
);

export const selectZeroQuantitySearchInputUUIDs = (
  state: OrderInboundDetailState,
): string[] => {
  const zeroQuantitySearchInputUUIDs: string[] = [];
  for (const pendingItem of state.pendingQuantityMap.values()) {
    if (pendingItem.quantity === 0 && pendingItem.searchInputUUID) {
      zeroQuantitySearchInputUUIDs.push(pendingItem.searchInputUUID);
    }
  }
  return zeroQuantitySearchInputUUIDs;
};
